import { CheckCircleIcon, NoSymbolIcon } from '@heroicons/react/16/solid'
import { ArrowsRightLeftIcon } from '@heroicons/react/24/outline'
import type { FileForProcessing } from 'Model/index.js'
import { isSampleReadyForSubmission } from 'Model/index.js'
import { CollapsibleSpan } from '../../Page/OrderManagementPage/Modals/PipelineInformationModal.js'
import { useDispatch, useStore } from 'react-redux'
import Actions from '../../../Flux/index.js'
import { orderSlice } from '../../../Flux/Slice/Order/OrderSlice.js'
import type { ISampleMetaData } from '../../../Model/Sample/ISampleMetaData.js'
import type { ColumnKey } from './SampleDataColumns.js'
import { PopoutInput } from './PopoutInput.js'
import { HabitatTypes } from '../../../Generated/HabitatType.js'
import type { EnumDTOBaseType } from '../../Page/HomePage/Modals/MetadataHandling/ForEnums/CSVEnumSelector.js'
import { LocalityTypes } from '../../../Generated/LocalityType.js'
import { SequencingChemistrys } from '../../../Generated/SequencingChemistry.js'
import { Sequencers } from '../../../Generated/Sequencer.js'
import { CollectionTypes } from '../../../Generated/CollectionType.js'
import { TrapTypes } from '../../../Generated/TrapType.js'
import { ExtractionKits } from '../../../Generated/ExtractionKit.js'
import { MatrixTypes } from '../../../Generated/MatrixType.js'
import { formatDate, parseDate } from '../../../Util/DateHandling.js'
import type { JSX } from 'react'
import React, { useState } from 'react'
import { TooltipTrigger } from '../../../Provider/TooltipManager.js'
import type { AdjustedSample } from './SampleTable.js'
import { CustomCell } from './CustomCell.js'
import { metadataDeleteChoiceMessage } from './MetadataDeleteMessage.js'
import { useGetOrderSelector } from '../../../Flux/Selector/Order/GetOrder/GetOrderSelector.js'
import { trackOrderAction, type OrderAction } from '../../../Services/Telemetry/index.js'
import { XMarkIcon } from '@heroicons/react/24/outline'
import type { ReactNode } from 'react'
import type { PureMetadata } from '../../../Model/Sample/ISampleMetaData.js'
import { fieldsClearedBy, withFieldsCleared } from '../../../Model/Sample/metadataClearing.js'
import { useConfirm } from '../ConfirmProvider.js'
import type { State } from '../../../Flux/Slice/index.js'
import { getSampleMetaDatas } from '../../../Flux/Selector/Order/GetSampleMetaDatas/GetSampleMetaDataSelector.js'
import { getSamplePools } from '../../../Flux/Selector/Order/GetSamplePool/GetSamplePoolSelector.js'
import { currentMetadatas, currentPoolItem } from '../../../Flux/Selector/Order/CurrentOrderState.js'
import { lacksReverseFile } from '../../../Util/reverseFilePairing.js'
import { AddReverseFileModal } from './AddReverseFileModal.js'
import { CoordinatesLabel } from './CoordinatesLabel.js'

export function SampleTableRow(props: {
  sampleOrNC: AdjustedSample
  orderId: string
  poolId: string
  editable: boolean
  /** Whether METADATA may be edited — a wider rule than `editable`; see SampleTable. */
  metadataEditable: boolean
  columns: ColumnKey[]
  onSelect: (selected: boolean) => void
  isSelected: boolean
  filesExpanded: boolean
  setFilesExpanded: React.Dispatch<React.SetStateAction<boolean>>
  customState: boolean
  setCustomState: React.Dispatch<React.SetStateAction<boolean>>
  isMetadataTab?: boolean
}) {
  const dispatch = useDispatch()
  const order = useGetOrderSelector()
  const loadedOrder = order && order !== 'LOADING' && order.id === props.orderId ? order : undefined
  const missingReverse = !!loadedOrder && lacksReverseFile(props.sampleOrNC, loadedOrder.sampleConfiguration)
  const [addReverseOpen, setAddReverseOpen] = useState(false)
  const { confirm, choose } = useConfirm()
  const store = useStore<typeof State>()
  /* these run when a confirm dialog closes: re-read what they write back (see CurrentOrderState) */
  const currentMeta = (meta: ISampleMetaData) => currentMetadatas(getSampleMetaDatas(store.getState()), [meta])[0]
  const currentVersion = () =>
    currentPoolItem(getSamplePools(store.getState()), props.sampleOrNC.id)?.version ?? props.sampleOrNC.version ?? 'error'

  const deleteMetadataRow = async () => {
    const meta = props.sampleOrNC.sampleMetadata
    if (!meta) return
    await confirm(
      {
        title: 'Delete this metadata entry?',
        message: `This permanently removes the metadata "${meta.customerSampleName}". This cannot be undone.`,
        confirmLabel: 'Delete'
      },
      () => {
        dispatch(Actions[orderSlice.name].deleteMetadataItemRequest({ orderId: props.orderId, metadataId: meta.id }))
      }
    )
  }

  const unlinkMetadataRow = () => {
    const meta = props.sampleOrNC.sampleMetadata
    if (!meta) return
    dispatch(Actions[orderSlice.name].unlinkMetadatasRequest({ orderId: props.orderId, metadatas: [meta] }))
  }

  /**
   * Delete this sample. If it has metadata merged to it, first ask what should happen to that
   * metadata (delete it, or unlink it so its values survive as an unmerged row that can re-match a
   * re-uploaded sample) — mirroring the pool-delete flow. Without metadata we delete straight away.
   */
  const deleteSampleRow = () => {
    trackRow('delete_pool_item')
    const removeSample = () =>
      dispatch(
        Actions[orderSlice.name].deletePoolItemRequest({
          orderId: props.orderId,
          poolId: props.poolId,
          itemId: props.sampleOrNC.id,
          version: currentVersion()
        })
      )
    const meta = props.sampleOrNC.sampleMetadata
    if (!meta?.sampleId) {
      removeSample()
      return
    }
    choose(
      {
        title: 'Do you really want to delete this sample?',
        message: metadataDeleteChoiceMessage(1, 1)
      },
      [
        {
          label: 'Delete Metadata',
          testId: 'delete-sample-metadata-choice',
          className: 'btn-primary',
          value: 1,
          operation: () => {
            dispatch(Actions[orderSlice.name].deleteMetadataItemRequest({ orderId: props.orderId, metadataId: meta.id }))
          }
        },
        {
          label: 'Unlink Metadata',
          testId: 'unlink-sample-metadata-choice',
          className: 'btn-primary',
          value: 2,
          operation: () => {
            dispatch(Actions[orderSlice.name].unlinkMetadatasRequest({ orderId: props.orderId, metadatas: [currentMeta(meta)] }))
          }
        }
      ],
      async () => {
        removeSample()
      }
    )
  }

  /** Fire an order_view.action for this row's order, with role, when the order is loaded and matches. */
  const trackRow = (action: OrderAction) => {
    if (order && order !== 'LOADING' && order.id === props.orderId) trackOrderAction(action, order)
  }

  const updateEntry = (sampleOrNC: FileForProcessing) => {
    trackRow('update_sample_data')
    return dispatch(
      Actions[orderSlice.name].updatePoolItemRequest({ orderId: props.orderId, poolId: props.poolId, item: sampleOrNC })
    )
  }

  const updateMetadata = (m: ISampleMetaData) => {
    trackRow('update_sample_metadata')
    return dispatch(
      Actions[orderSlice.name].batchUpdateSamples({
        orderId: props.orderId,
        poolId: props.poolId,
        ops: [{ op: 'UpdateMetadata', orderId: props.orderId, poolId: props.poolId, metadata: m }]
      })
    )
  }

  /**
   * Empties one metadata field. The stored value is the only way most of these can be corrected — the
   * table shows them read-only and the importer can only overwrite — so this is a deliberate reset
   * rather than an edit. The field is deleted rather than set to undefined so it is absent from the
   * request body, which the whole-row PUT reads as null.
   */
  const clearMetadataFields = (column: ColumnKey, fields: readonly (keyof PureMetadata)[]) => {
    const meta = props.sampleOrNC.sampleMetadata
    if (!meta || fields.length === 0) {
      return
    }
    /* There is no undo, and the stored value is often the only copy — a customer's sampling date is
       not re-derivable from anything on screen. */
    void confirm(
      {
        title: 'Clear this value?',
        message: `${column} will be emptied for ${meta.customerSampleName || 'this sample'}. This cannot be undone.`,
        confirmLabel: 'Clear'
      },
      () => {
        updateMetadata(withFieldsCleared(currentMeta(meta), fields))
      }
    )
  }

  /**
   * A read-only metadata cell with a reset affordance.
   *
   * The button is overlaid rather than placed in the flow: thirteen columns each reserving room for it
   * would cost the table a couple of hundred pixels of width it does not have. Opacity rather than
   * `invisible` keeps it reachable by keyboard, and it can only be hovered while the cell is hovered,
   * so it is never interacted with unseen.
   */
  const clearableCell = (key: string, column: ColumnKey, content: ReactNode, hasValue: boolean, className?: string) => (
    <td key={key} className={`group relative ${className ?? ''}`}>
      {content}
      {props.metadataEditable && hasValue && (
        <button
          type="button"
          aria-label={`Clear ${column}`}
          title="Clear this value"
          data-testid={`clear-metadata-${column}`}
          className="absolute right-1 top-1/2 -translate-y-1/2 rounded bg-base-100/90 p-0.5 text-gray-400 opacity-0 transition-opacity hover:text-error focus:opacity-100 group-hover:opacity-100"
          onClick={() => clearMetadataFields(column, fieldsClearedBy(column))}
        >
          <XMarkIcon className="w-3 h-3" />
        </button>
      )}
    </td>
  )

  const toggleItemType = () => {
    trackRow('convert_pool_item')
    dispatch(
      Actions[orderSlice.name].convertPoolItemRequest({
        orderId: props.orderId,
        poolId: props.poolId,
        item: props.sampleOrNC
      })
    )
  }

  const renderMap: Record<ColumnKey, (key: string, column: ColumnKey) => JSX.Element> = {
    select: (key) => (
      <td key={key}>
        <input
          data-test-id={`select-sample-${props.sampleOrNC.id}-pool-${props.poolId}`}
          className="checkbox"
          type={'checkbox'}
          onChange={() => props.onSelect(!props.isSelected)}
          checked={props.isSelected}
        />
      </td>
    ),
    mergeStatus: (key) => (
      <td key={key}>
        {props.sampleOrNC.sampleMetadata?.sampleId ? (
          <CheckCircleIcon className="fill-green-500 w-3 h-3" />
        ) : (
          <NoSymbolIcon className="fill-yellow-500 h-3 w-3" />
        )}
      </td>
    ),
    pipelineStatus: (key) => (
      <td key={key}>
        {props.sampleOrNC.pipelineInformation || isSampleReadyForSubmission(props.sampleOrNC) ? (
          <CheckCircleIcon className="fill-green-500 w-3 h-3" />
        ) : (
          <NoSymbolIcon className="fill-yellow-500 h-3 w-3" />
        )}
      </td>
    ),
    otuCount: (key) => <td key={key}>{props.sampleOrNC.otuCount}</td>,
    itemType: (key) => (
      <td key={key}>
        {props.editable ? (
          <TooltipTrigger
            tooltip={
              props.sampleOrNC.itemType === 'SAMPLE'
                ? 'Convert to negative control. Can be converted back.'
                : 'Convert back to sample. Sample metadata is restored.'
            }
          >
            <button
              type="button"
              className="inline-flex items-center gap-1 rounded border border-slate-300 px-2 py-1 text-xs font-medium hover:bg-slate-100"
              onClick={toggleItemType}
            >
              <span>{props.sampleOrNC.itemType === 'SAMPLE' ? 'S' : 'NC'}</span>
              <ArrowsRightLeftIcon className="h-3.5 w-3.5" />
            </button>
          </TooltipTrigger>
        ) : (
          <span>{props.sampleOrNC.itemType === 'SAMPLE' ? 'S' : 'NC'}</span>
        )}
      </td>
    ),
    customerSampleName: (key) => (
      <td key={key} className="w-fit text-ellipsis overflow-auto">
        {props.sampleOrNC.itemType === 'SAMPLE' ? (
          <PopoutInput
            initialValue={props.sampleOrNC.customerSampleName ?? ''}
            editable={props.metadataEditable && !!props.sampleOrNC.sampleMetadata}
            updateValue={(v) =>
              props.sampleOrNC.sampleMetadata
                ? updateMetadata({ ...props.sampleOrNC.sampleMetadata, customerSampleName: v ?? '' })
                : console.error('should not happen')
            }
          />
        ) : (
          <span></span>
        )}
      </td>
    ),
    files: (key) => (
      <td key={key}>
        <div className="flex-col">
          <div>
            <CollapsibleSpan
              maxChars={8}
              content={props.sampleOrNC.forwardFile?.fileName ?? ''}
              collapsedContent={props.sampleOrNC.forwardFileNameStripped ?? ''}
              expanded={props.filesExpanded}
              onExpand={props.setFilesExpanded}
            />
          </div>
          {loadedOrder && missingReverse ? (
            <div className="flex items-center gap-1 rounded px-1 shadow-inner shadow-red-300" data-testid="missing-reverse-file">
              <span className="text-xs text-error">R2 missing</span>
              {props.editable && (
                <button
                  type="button"
                  className="btn btn-xs btn-outline btn-error"
                  data-testid={`add-reverse-file-${props.sampleOrNC.id}`}
                  onClick={() => setAddReverseOpen(true)}
                >
                  Add R2
                </button>
              )}
              {addReverseOpen && (
                <AddReverseFileModal
                  isOpen={addReverseOpen}
                  onClose={() => setAddReverseOpen(false)}
                  sample={props.sampleOrNC}
                  order={loadedOrder}
                />
              )}
            </div>
          ) : (
            <div>
              <CollapsibleSpan
                maxChars={8}
                content={props.sampleOrNC.reverseFile?.fileName ?? ''}
                collapsedContent={props.sampleOrNC.reverseFileNameStripped ?? ''}
                expanded={props.filesExpanded}
                onExpand={props.setFilesExpanded}
              />
            </div>
          )}
        </div>
      </td>
    ),
    literature: (key) => (
      <td key={key}>
        <CollapsibleSpan maxChars={10} content={props.sampleOrNC.primerLiterature ?? ''} />
      </td>
    ),
    forwardPrimer: (key) => (
      <td
        key={key}
        className={`max-w-4 text-ellipsis overflow-auto ${!props.sampleOrNC.forwardPrimer && 'shadow-red-300 shadow-inner border-r-2'}`}
      >
        <PopoutInput
          initialValue={props.sampleOrNC.forwardPrimer}
          editable={props.editable}
          updateValue={(v) => updateEntry({ ...props.sampleOrNC, forwardPrimer: v })}
        />
      </td>
    ),
    reversePrimer: (key) => (
      <td
        key={key}
        className={`max-w-4 text-ellipsis overflow-auto ${!props.sampleOrNC.reversePrimer && 'shadow-red-300 shadow-inner border-r-2'}`}
      >
        <PopoutInput
          initialValue={props.sampleOrNC.reversePrimer}
          editable={props.editable}
          updateValue={(v) => updateEntry({ ...props.sampleOrNC, reversePrimer: v })}
        />
      </td>
    ),
    forwardPrimerRequested: (key) => (
      <td
        key={key}
        className={`max-w-4 text-ellipsis overflow-auto ${!props.sampleOrNC.sampleMetadata?.forwardPrimerRequested && 'shadow-red-300 shadow-inner border-r-2'}`}
      >
        <PopoutInput
          initialValue={props.sampleOrNC.sampleMetadata?.forwardPrimerRequested}
          editable={false} /* customer-requested primer is informational only; editing is not supported */
          updateValue={() => console.error('unsupported!')}
        />
      </td>
    ),
    reversePrimerRequested: (key) => (
      <td
        key={key}
        className={`max-w-4 text-ellipsis overflow-auto ${!props.sampleOrNC.sampleMetadata?.reversePrimerRequested && 'shadow-red-300 shadow-inner border-r-2'}`}
      >
        <PopoutInput
          initialValue={props.sampleOrNC.sampleMetadata?.reversePrimerRequested}
          editable={false} /* customer-requested primer is informational only; editing is not supported */
          updateValue={() => console.error('unsupported!')}
        />
      </td>
    ),
    sampleCoordinates: (key, column) =>
      clearableCell(
        key,
        column,
        <CoordinatesLabel coordinates={props.sampleOrNC.sampleMetadata?.sampleCoordinates} />,
        !!props.sampleOrNC.sampleMetadata?.sampleCoordinates,
        'max-w-4 text-ellipsis overflow-auto'
      ),
    samplingDate: (key, column) => clearableCell(key, column, dateLabel(props.sampleOrNC.sampleMetadata?.samplingDate), !!props.sampleOrNC.sampleMetadata?.samplingDate),
    samplingStart: (key, column) => clearableCell(key, column, dateLabel(props.sampleOrNC.sampleMetadata?.samplingStart), !!props.sampleOrNC.sampleMetadata?.samplingStart),
    samplingEnd: (key, column) => clearableCell(key, column, dateLabel(props.sampleOrNC.sampleMetadata?.samplingEnd), !!props.sampleOrNC.sampleMetadata?.samplingEnd),
    matrixType: (key, column) => clearableCell(key, column, enumLabel(props.sampleOrNC.sampleMetadata?.matrixType, MatrixTypes), !!props.sampleOrNC.sampleMetadata?.matrixType),
    extractionKit: (key, column) => clearableCell(key, column, enumLabel(props.sampleOrNC.sampleMetadata?.extractionKit, ExtractionKits), !!props.sampleOrNC.sampleMetadata?.extractionKit),
    trapType: (key, column) => clearableCell(key, column, enumLabel(props.sampleOrNC.sampleMetadata?.trapType, TrapTypes), !!props.sampleOrNC.sampleMetadata?.trapType),
    collectionType: (key, column) => clearableCell(key, column, enumLabel(props.sampleOrNC.sampleMetadata?.collectionType, CollectionTypes), !!props.sampleOrNC.sampleMetadata?.collectionType),
    sequencer: (key, column) => clearableCell(key, column, enumLabel(props.sampleOrNC.sampleMetadata?.sequencer, Sequencers), !!props.sampleOrNC.sampleMetadata?.sequencer),
    sequencingChemistry: (key, column) => clearableCell(key, column, enumLabel(props.sampleOrNC.sampleMetadata?.sequencingChemistry, SequencingChemistrys), !!props.sampleOrNC.sampleMetadata?.sequencingChemistry),
    sequencingDepth: (key, column) => clearableCell(key, column, props.sampleOrNC.sampleMetadata?.sequencingDepth, !!props.sampleOrNC.sampleMetadata?.sequencingDepth),
    localityType: (key, column) => clearableCell(key, column, enumLabel(props.sampleOrNC.sampleMetadata?.localityType, LocalityTypes), !!props.sampleOrNC.sampleMetadata?.localityType),
    habitatType: (key, column) => clearableCell(key, column, enumLabel(props.sampleOrNC.sampleMetadata?.habitatType, HabitatTypes), !!props.sampleOrNC.sampleMetadata?.habitatType),
    custom: (key) => (
      <CustomCell
        key={key}
        value={props.sampleOrNC.sampleMetadata?.customAttributes}
        isExpanded={props.customState}
        setExpanded={props.setCustomState}
      />
    ),
    actions: (key) =>
      props.isMetadataTab ? (
        <td key={key} className="text-ellipsis">
          <div className="flex items-center gap-1">
            {/* Unlink only makes sense for merged metadata (sampleId set); it detaches the row
                without losing its values so it can re-match a re-uploaded sample. */}
            {props.sampleOrNC.sampleMetadata?.sampleId && (
              <button
                data-test-id={`unlink-metadata-${props.sampleOrNC.sampleMetadata.id}`}
                className="btn btn-xs"
                title="Detach this metadata from its sample (keeps the row as unmerged)"
                onClick={unlinkMetadataRow}
              >
                Unlink
              </button>
            )}
            <button
              data-test-id={`delete-metadata-${props.sampleOrNC.sampleMetadata?.id}`}
              className="btn btn-xs btn-error"
              title="Delete this metadata entry permanently"
              onClick={deleteMetadataRow}
            >
              Delete
            </button>
          </div>
        </td>
      ) : (
        <td key={key} className="text-ellipsis">
          <button onClick={deleteSampleRow}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.5}
              stroke="currentColor"
              className="size-6"
            >
              <path strokeLinecap="round" strokeLinejoin="round" d="M15 12H9m12 0a9 9 0 1 1-18 0 9 9 0 0 1 18 0Z" />
            </svg>
          </button>
        </td>
      ),
    filler: (key) => <td key={key} />
  }

  return (
    <tr data-testid={'table-row'}>
      {props.columns.map((columnType) => renderMap[columnType](`cell-${columnType}-${props.sampleOrNC.id}`, columnType))}
    </tr>
  )
}

function enumLabel<TKey extends string>(field: TKey | undefined, enumRecord: Record<TKey, EnumDTOBaseType>) {
  return field ? enumRecord[field]?.label : ''
}

function dateLabel(value?: string) {
  return value ? <TooltipTrigger tooltip={value}>{formatDate(parseDate(value))}</TooltipTrigger> : ''
}
